import React, { useState, useEffect } from 'react';
import { ArrowRight, Heart, Star, Quote, Truck, RefreshCw, Shield, Clock, Sparkles, Gift, Crown, ChevronLeft, ChevronRight, Instagram, Diamond } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 9: STILETTO
 * Style: Glamorous, Feminine, Romantic
 * Best for: Women's party wear, Indo-western fusion, Festive collection
 * Light theme with soft pink/fuchsia accents
 */
const Template9 = () => {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);

    // Hero slides
    const heroSlides = [
        {
            title: 'Glamour Redefined',
            subtitle: 'Party Collection 2024',
            image: 'https://placehold.co/1920x1080/FDF4FF/A21CAF?text=Glamour+Campaign'
        },
        {
            title: 'Desi Diva',
            subtitle: 'Indo-Western Fusion',
            image: 'https://placehold.co/1920x1080/FCE7F3/BE185D?text=Fusion+Campaign'
        },
        {
            title: 'Festive Glam',
            subtitle: 'Celebrate in Style',
            image: 'https://placehold.co/1920x1080/FDF2F8/DB2777?text=Festive+Campaign'
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 4000);
        return () => clearInterval(timer);
    }, []);

    // Categories
    const categories = [
        { name: 'Party Dresses', count: '150+', image: 'https://placehold.co/400x500/FDF4FF/A21CAF?text=Party+Dress', icon: Crown },
        { name: 'Indo-Western', count: '200+', image: 'https://placehold.co/400x500/FCE7F3/BE185D?text=Indo-Western', icon: Sparkles },
        { name: 'Cocktail Sarees', count: '120+', image: 'https://placehold.co/400x500/FDF2F8/DB2777?text=Cocktail+Saree', icon: Diamond },
        { name: 'Gowns', count: '80+', image: 'https://placehold.co/400x500/FBCFE8/9D174D?text=Gowns', icon: Gift }
    ];

    // Featured products
    const products = [
        { id: 1, name: 'Sequin Embellished Gown', price: '₹8,999', originalPrice: '₹12,999', image: 'https://placehold.co/400x500/FDF4FF/A21CAF?text=Gown+1', badge: 'Bestseller', color: 'Rose Pink' },
        { id: 2, name: 'Fusion Lehenga Saree', price: '₹11,499', originalPrice: '₹15,999', image: 'https://placehold.co/400x500/FCE7F3/BE185D?text=Lehenga+1', badge: 'New', color: 'Magenta' },
        { id: 3, name: 'Cape Style Anarkali', price: '₹7,299', originalPrice: '₹9,999', image: 'https://placehold.co/400x500/FDF2F8/DB2777?text=Anarkali+1', badge: 'Trending', color: 'Blush' },
        { id: 4, name: 'Shimmer Palazzo Set', price: '₹5,499', originalPrice: '₹7,499', image: 'https://placehold.co/400x500/FBCFE8/9D174D?text=Palazzo+1', badge: 'Party', color: 'Gold' },
        { id: 5, name: 'Pre-draped Saree Gown', price: '₹13,999', originalPrice: '₹18,999', image: 'https://placehold.co/400x500/FDF4FF/A21CAF?text=Saree+Gown', badge: 'Premium', color: 'Wine' },
        { id: 6, name: 'Mirror Work Crop Top Set', price: '₹6,799', originalPrice: '₹8,999', image: 'https://placehold.co/400x500/FCE7F3/BE185D?text=Crop+Set', badge: 'Festive', color: 'Hot Pink' }
    ];

    // Testimonials
    const testimonials = [
        { text: 'The sequin gown I wore for my sangeet was absolutely stunning! Received so many compliments. The fit was perfect.', name: 'Neha Kapoor', location: 'Delhi', event: 'Sangeet Night' },
        { text: 'Finally found a brand that understands what modern Indian women want. Their Indo-western collection is pure magic!', name: 'Priyanka Deshmukh', location: 'Mumbai', event: 'Cocktail Party' },
        { text: 'The pre-draped saree gown made me feel like a diva at my friend\'s reception. Easy to wear and absolutely gorgeous.', name: 'Tanvi Reddy', location: 'Hyderabad', event: 'Wedding Reception' }
    ];

    // Lookbook images
    const lookbook = [
        { image: 'https://placehold.co/400x600/FDF4FF/A21CAF?text=Look+1', title: 'Sangeet Ready' },
        { image: 'https://placehold.co/400x600/FCE7F3/BE185D?text=Look+2', title: 'Cocktail Queen' },
        { image: 'https://placehold.co/400x600/FDF2F8/DB2777?text=Look+3', title: 'Reception Glam' },
        { image: 'https://placehold.co/400x600/FBCFE8/9D174D?text=Look+4', title: 'Party Perfect' }
    ];

    // Instagram posts
    const instagramPosts = [
        { image: 'https://placehold.co/300x300/FDF4FF/A21CAF?text=Style+1', likes: '3.2k' },
        { image: 'https://placehold.co/300x300/FCE7F3/BE185D?text=Style+2', likes: '2.8k' },
        { image: 'https://placehold.co/300x300/FDF2F8/DB2777?text=Style+3', likes: '4.1k' },
        { image: 'https://placehold.co/300x300/FBCFE8/9D174D?text=Style+4', likes: '2.5k' },
        { image: 'https://placehold.co/300x300/FDF4FF/A21CAF?text=Style+5', likes: '3.7k' },
        { image: 'https://placehold.co/300x300/FCE7F3/BE185D?text=Style+6', likes: '2.9k' }
    ];

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Hero Section - Full Screen with Romantic Feel */}
            <section className="relative h-screen overflow-hidden">
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-all duration-1000 ${
                            currentSlide === index ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                        }`}
                    >
                        <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-950/50 via-pink-900/30 to-transparent" />
                    </div>
                ))}

                {/* Hero Content */}
                <div className="absolute inset-0 flex items-center z-10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                        <div className="max-w-2xl">
                            <div className="flex items-center gap-2 mb-4">
                                <Sparkles className="w-5 h-5 text-pink-300" />
                                <span className="text-pink-200 text-sm font-medium tracking-[0.3em] uppercase">
                                    {heroSlides[currentSlide].subtitle}
                                </span>
                            </div>
                            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[1.1] mb-6">
                                {heroSlides[currentSlide].title.split(' ').map((word, i) => (
                                    <span key={i} className={i === 1 ? 'font-serif italic text-pink-200 block' : 'block'}>
                                        {word}
                                    </span>
                                ))}
                            </h1>
                            <p className="text-pink-100 text-lg lg:text-xl mb-8 max-w-md">
                                Unleash your inner diva with our exclusive collection of glamorous party wear designed for the modern Indian woman.
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => navigate('/template9/shop')}
                                    className="group px-8 py-4 bg-white text-fuchsia-900 text-sm font-semibold tracking-wider uppercase hover:bg-pink-50 transition-all duration-300 rounded-full flex items-center gap-3"
                                >
                                    Shop Collection
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="px-8 py-4 border-2 border-white/50 text-white text-sm font-medium tracking-wider uppercase hover:bg-white/10 transition-all duration-300 rounded-full">
                                    View Lookbook
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Slide Indicators */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                currentSlide === index ? 'w-8 bg-white' : 'w-2 bg-white/40'
                            }`}
                        />
                    ))}
                </div>

                {/* Decorative Element */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
            </section>

            {/* Intro Section */}
            <section className="py-20 lg:py-24 bg-gradient-to-b from-white to-pink-50/50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full mb-6">
                        <Crown className="w-4 h-4 text-fuchsia-600" />
                        <span className="text-fuchsia-700 text-sm font-medium">For The Diva In You</span>
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-light text-fuchsia-950 mb-6 leading-relaxed">
                        Every woman deserves to feel <span className="font-serif italic text-fuchsia-600">glamorous</span>.
                        We create fashion that makes you the <span className="font-serif italic text-fuchsia-600">star</span> of every event.
                    </h2>
                    <p className="text-fuchsia-700/70 text-lg">
                        From sangeet nights to cocktail parties, find your perfect show-stopping outfit.
                    </p>
                </div>
            </section>

            {/* Categories */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-fuchsia-500 text-sm font-medium tracking-[0.2em] uppercase">Shop By Occasion</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-fuchsia-950 mt-3">Curated Collections</h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template9/shop')}
                                className="group cursor-pointer relative overflow-hidden rounded-3xl bg-gradient-to-br from-pink-100 to-fuchsia-100 p-1"
                            >
                                <div className="relative overflow-hidden rounded-[22px]">
                                    <div className="aspect-[4/5] overflow-hidden">
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-fuchsia-900/70 via-fuchsia-800/20 to-transparent" />
                                    <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                        <category.icon className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                        <h3 className="text-white text-lg lg:text-xl font-medium">{category.name}</h3>
                                        <p className="text-pink-200 text-sm mt-1">{category.count} Styles</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-16 lg:py-24 bg-gradient-to-b from-pink-50/50 to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-fuchsia-500 text-sm font-medium tracking-[0.2em] uppercase">Hot Right Now</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-fuchsia-950 mt-3">Trending Styles</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template9/shop')}
                            className="group flex items-center gap-2 text-fuchsia-700 font-medium mt-4 lg:mt-0 hover:text-fuchsia-900 transition-colors"
                        >
                            View All Products
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template9/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden bg-pink-50 rounded-2xl mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <span className="absolute top-3 left-3 px-3 py-1.5 bg-fuchsia-500 text-white text-xs font-semibold rounded-full">
                                        {product.badge}
                                    </span>
                                    <button className="absolute top-3 right-3 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:text-fuchsia-600 shadow-lg">
                                        <Heart className="w-5 h-5" />
                                    </button>
                                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-fuchsia-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button className="w-full py-3 bg-white text-fuchsia-900 text-sm font-semibold rounded-full hover:bg-pink-50 transition-colors">
                                            Quick Add
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="w-4 h-4 rounded-full bg-gradient-to-br from-pink-300 to-fuchsia-400" />
                                        <span className="text-fuchsia-600/70 text-xs">{product.color}</span>
                                    </div>
                                    <h3 className="text-fuchsia-950 font-medium group-hover:text-fuchsia-600 transition-colors line-clamp-1">
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-fuchsia-900 font-bold text-lg">{product.price}</span>
                                        <span className="text-fuchsia-400 text-sm line-through">{product.originalPrice}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lookbook Section */}
            <section className="py-16 lg:py-24 bg-fuchsia-950 text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-pink-300 text-sm font-medium tracking-[0.2em] uppercase">Style Inspiration</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-white mt-3">The Lookbook</h2>
                        </div>
                        <button className="group flex items-center gap-2 text-pink-300 font-medium mt-4 lg:mt-0 hover:text-white transition-colors">
                            View Full Lookbook
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {lookbook.map((look, index) => (
                            <div
                                key={index}
                                className="group relative aspect-[2/3] overflow-hidden rounded-2xl cursor-pointer"
                            >
                                <img
                                    src={look.image}
                                    alt={look.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-fuchsia-900/80 via-transparent to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <p className="text-white text-lg font-medium">{look.title}</p>
                                    <p className="text-pink-200 text-sm">Shop the look</p>
                                </div>
                                <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                                    <ArrowRight className="w-4 h-4 text-white" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-fuchsia-500 text-sm font-medium tracking-[0.2em] uppercase">The Stiletto Promise</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-fuchsia-950 mt-3">Why Women Love Us</h2>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Diamond className="w-8 h-8 text-fuchsia-600" />
                            </div>
                            <h3 className="text-fuchsia-950 font-medium text-lg mb-2">Premium Quality</h3>
                            <p className="text-fuchsia-700/70 text-sm">Finest fabrics and impeccable craftsmanship in every piece</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-8 h-8 text-fuchsia-600" />
                            </div>
                            <h3 className="text-fuchsia-950 font-medium text-lg mb-2">Exclusive Designs</h3>
                            <p className="text-fuchsia-700/70 text-sm">Unique styles you won't find anywhere else</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Crown className="w-8 h-8 text-fuchsia-600" />
                            </div>
                            <h3 className="text-fuchsia-950 font-medium text-lg mb-2">Perfect Fit</h3>
                            <p className="text-fuchsia-700/70 text-sm">Expert sizing with free alterations on select items</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Gift className="w-8 h-8 text-fuchsia-600" />
                            </div>
                            <h3 className="text-fuchsia-950 font-medium text-lg mb-2">Luxe Packaging</h3>
                            <p className="text-fuchsia-700/70 text-sm">Every order arrives gift-ready in premium packaging</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 lg:py-24 bg-gradient-to-br from-pink-50 to-fuchsia-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-fuchsia-500 text-sm font-medium tracking-[0.2em] uppercase">Real Stories</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-fuchsia-950 mt-3">Divas Who Loved Us</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl transition-shadow duration-300">
                                <div className="flex gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-fuchsia-400 text-fuchsia-400" />
                                    ))}
                                </div>
                                <p className="text-fuchsia-800/80 leading-relaxed mb-6 italic">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center gap-4 pt-4 border-t border-pink-100">
                                    <div className="w-12 h-12 bg-gradient-to-br from-pink-200 to-fuchsia-200 rounded-full flex items-center justify-center">
                                        <span className="text-fuchsia-700 font-semibold">{testimonial.name[0]}</span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-fuchsia-950">{testimonial.name}</p>
                                        <p className="text-fuchsia-600/70 text-sm">{testimonial.event} • {testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="py-16 lg:py-24 bg-gradient-to-br from-fuchsia-500 via-pink-500 to-rose-400 text-white relative overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-10 left-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                <div className="absolute bottom-10 right-10 w-60 h-60 bg-white/10 rounded-full blur-3xl" />

                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <Sparkles className="w-10 h-10 text-white/80 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl font-light text-white mb-4">
                        Join the Glam Squad
                    </h2>
                    <p className="text-white/90 text-lg mb-8">
                        Get exclusive access to new arrivals, styling tips, and members-only offers.
                        Plus, enjoy 20% off your first glamorous purchase!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-white placeholder-white/70 focus:outline-none focus:bg-white/30 transition-colors"
                        />
                        <button className="px-8 py-4 bg-white text-fuchsia-600 text-sm font-semibold tracking-wider uppercase hover:bg-pink-50 transition-colors rounded-full whitespace-nowrap shadow-lg">
                            Subscribe
                        </button>
                    </div>
                    <p className="text-white/70 text-sm mt-4">
                        No spam. Just glamour delivered to your inbox.
                    </p>
                </div>
            </section>

            {/* Instagram */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-6 h-6 text-fuchsia-600" />
                            <span className="text-fuchsia-600 font-medium">@stiletto_fashion</span>
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-light text-fuchsia-950">#StilettoGlam</h2>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                        {instagramPosts.map((post, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer">
                                <img
                                    src={post.image}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-fuchsia-600/0 group-hover:bg-fuchsia-600/40 transition-colors duration-300 flex items-center justify-center">
                                    <div className="flex items-center gap-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Heart className="w-5 h-5 fill-white" />
                                        <span className="text-sm font-medium">{post.likes}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="py-12 bg-pink-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Truck className="w-5 h-5 text-fuchsia-600" />
                            </div>
                            <div>
                                <p className="font-medium text-fuchsia-950">Free Shipping</p>
                                <p className="text-fuchsia-600/70 text-sm">On orders above ₹2,499</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <RefreshCw className="w-5 h-5 text-fuchsia-600" />
                            </div>
                            <div>
                                <p className="font-medium text-fuchsia-950">Easy Returns</p>
                                <p className="text-fuchsia-600/70 text-sm">10-day return policy</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Shield className="w-5 h-5 text-fuchsia-600" />
                            </div>
                            <div>
                                <p className="font-medium text-fuchsia-950">Secure Payment</p>
                                <p className="text-fuchsia-600/70 text-sm">100% secure checkout</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Clock className="w-5 h-5 text-fuchsia-600" />
                            </div>
                            <div>
                                <p className="font-medium text-fuchsia-950">COD Available</p>
                                <p className="text-fuchsia-600/70 text-sm">Pay on delivery</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template9;
